import * as m from '@staiter/i18n';
import type { Tier3OnecPushEventRow } from '@staiter/shared';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Tier3SendAction } from '~/components/approvals';
import { PageHeader, Table, type TableColumn } from '~/components/data';
import { Input, Select, Textarea } from '~/components/primitives';
import {
  type Tier3Subject,
  fetchTier3OnecPushHistory,
  fetchTier3Preview,
  tier3PreviewKey,
} from '~/lib/api/tier3';
import { formatDate } from '~/lib/format';
import { businessTodayIso } from '~/lib/tasks/business-day';
import styles from './_auth.finance.onec-push.module.css';

/**
 * F91 пака `tails-closeout` (Ф6, SOLUTION §9.3 п. 3, К3-7) — запуск отправок 1–4 в 1С.
 *
 * Раньше отправки мастер-данных, агрегатов, документов и откат жили на `/admin/integrations`
 * и `/admin/sync-staging` под админ-гейтом — бухгалтер, который их и подписывает, туда не
 * попадал. Здесь экран под модулем финансов; каждая отправка — заявка уровня 3 с двумя
 * подписями (кнопка видна всегда, недоступность — с причиной). Мастер-данные сервер
 * дополнительно проверяет модулем склада.
 */
export const Route = createFileRoute('/_auth/finance/onec-push')({ component: OnecPushPage });

const MASTER_RESOURCES = ['counterparty', 'item', 'warehouse', 'bom'] as const;
const MASTER_SUBJECT: Tier3Subject = {
  taskType: 'master_data_push_1c',
  subjectKind: 'master_data',
  payload: { resources: [...MASTER_RESOURCES] },
};
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function parseIds(raw: string): string[] {
  return [
    ...new Set(
      raw
        .split(/[\s,;]+/)
        .map((x) => x.trim())
        .filter((x) => UUID_RE.test(x)),
    ),
  ];
}

function OnecPushPage(): JSX.Element {
  const [pushType, setPushType] = useState<'sle_aggregates' | 'stock_entries' | 'landed_cost'>(
    'sle_aggregates',
  );
  // С-G Р.7 — месяц по умолчанию — от делового дня: 1-го числа до 05:00 по Алматы UTC давал
  // прошлый месяц.
  const [period, setPeriod] = useState(() => businessTodayIso().slice(0, 7));
  const [aggrIds, setAggrIds] = useState('');
  const [docIds, setDocIds] = useState('');
  const aggr = useMemo(() => parseIds(aggrIds), [aggrIds]);
  const docs = useMemo(() => parseIds(docIds), [docIds]);
  const history = useQuery({
    queryKey: ['tier3', 'onec-push-history'],
    queryFn: fetchTier3OnecPushHistory,
  });
  /**
   * Круг 2 гейта Ф6 (K3 UX-1): у пустой карточки причина называет ВСЕ блокеры сразу — и
   * «укажите документы», и «1С не подключена», а не второй после ввода. Состояние 1С берём
   * из того же предпросмотра, что у мастер-данных (общий кэш запроса).
   */
  const masterPreview = useQuery({
    queryKey: tier3PreviewKey(MASTER_SUBJECT),
    queryFn: () => fetchTier3Preview(MASTER_SUBJECT),
    staleTime: 30_000,
    retry: false,
    meta: { suppressErrorToast: true },
  });
  const offline = masterPreview.data?.availability.reason === 'integration_not_connected';
  const blockedFor = (ids: readonly string[]): string | undefined =>
    ids.length > 0
      ? undefined
      : [
          m.onec_push_ids_missing(),
          ...(offline ? [m.tier3_send_reason_integration_not_connected()] : []),
        ].join(' · ');

  const columns: TableColumn<Tier3OnecPushEventRow>[] = [
    {
      id: 'created',
      header: m.onec_push_col_created(),
      accessor: (r) => <span className={styles.mono}>{formatDate(r.created_at)}</span>,
    },
    {
      id: 'kind',
      header: m.onec_push_col_kind(),
      accessor: (r) => m.tier3_action_name({ task_type: r.task_type }),
    },
    {
      id: 'status',
      header: m.onec_push_col_status(),
      accessor: (r) => m.onec_push_status_name({ status: r.status }),
    },
    {
      id: 'count',
      header: m.onec_push_col_count(),
      align: 'right',
      accessor: (r) => <span className={styles.num}>{r.source_count}</span>,
    },
    {
      id: 'actions',
      header: m.onec_push_col_actions(),
      accessor: (r) => (
        <Tier3SendAction
          variant="secondary"
          blockedReason={
            r.rollback_push_id !== null
              ? m.error_by_code('onec.already_rolled_back')
              : r.status !== 'pushed'
                ? m.error_by_code('onec.rollback_not_pushed')
                : undefined
          }
          label={m.onec_push_btn_rollback()}
          subject={{
            taskType: 'onec_push_rollback',
            subjectKind: 'onec_push_event',
            payload: { push_id: r.id },
          }}
          reasonField={{ key: 'reason', label: m.onec_push_rollback_reason(), minLength: 10 }}
        />
      ),
    },
  ];

  return (
    <div>
      <PageHeader title={m.onec_push_title()} description={m.onec_push_subtitle()} />
      {/* Круг 2 (K3 UX-4): где потом искать подписанную заранее заявку. */}
      <p className={styles.pendingLink}>
        <Link to="/approvals">{m.onec_push_pending_link()}</Link>
      </p>
      <div className={styles.grid}>
        <section className={styles.card} aria-labelledby="onec-master">
          <h2 id="onec-master" className={styles.cardTitle}>
            {m.onec_push_master_title()}
          </h2>
          <p className={styles.cardText}>{m.onec_push_master_text()}</p>
          <div className={styles.actions}>
            <Tier3SendAction
              align="start"
              label={m.onec_push_btn_master()}
              subject={MASTER_SUBJECT}
            />
          </div>
        </section>

        <section className={styles.card} aria-labelledby="onec-aggr">
          <h2 id="onec-aggr" className={styles.cardTitle}>
            {m.onec_push_aggr_title()}
          </h2>
          <div className={styles.fields}>
            <Select
              label={m.onec_push_aggr_type()}
              value={pushType}
              onChange={(e) => setPushType(e.target.value as typeof pushType)}
              options={[
                { value: 'sle_aggregates', label: m.onec_push_type_sle_aggregates() },
                { value: 'stock_entries', label: m.onec_push_type_stock_entries() },
                { value: 'landed_cost', label: m.onec_push_type_landed_cost() },
              ]}
            />
            <Input
              label={m.onec_push_period()}
              type="month"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
            />
          </div>
          <Textarea
            label={m.onec_push_ids_label()}
            hint={m.onec_push_ids_hint({ count: String(aggr.length) })}
            value={aggrIds}
            onChange={(e) => setAggrIds(e.target.value)}
            rows={3}
          />
          <div className={styles.actions}>
            <Tier3SendAction
              align="start"
              blockedReason={blockedFor(aggr)}
              label={m.onec_push_btn_aggr()}
              subject={{
                taskType: 'aggregates_push_1c',
                subjectKind: 'onec_batch',
                payload: { push_type: pushType, source_document_ids: aggr, period },
              }}
            />
          </div>
        </section>

        <section className={styles.card} aria-labelledby="onec-docs">
          <h2 id="onec-docs" className={styles.cardTitle}>
            {m.onec_push_docs_title()}
          </h2>
          <Textarea
            label={m.onec_push_ids_label()}
            hint={m.onec_push_ids_hint({ count: String(docs.length) })}
            value={docIds}
            onChange={(e) => setDocIds(e.target.value)}
            rows={3}
          />
          <div className={styles.actions}>
            <Tier3SendAction
              align="start"
              blockedReason={blockedFor(docs)}
              label={m.onec_push_btn_docs()}
              subject={{
                taskType: 'documents_push_1c',
                subjectKind: 'onec_batch',
                payload: { source_document_ids: docs },
              }}
            />
          </div>
        </section>
      </div>

      <section aria-labelledby="onec-history" className={styles.history}>
        <h2 id="onec-history" className={styles.cardTitle}>
          {m.onec_push_history_title()}
        </h2>
        <Table
          data={history.data?.items ?? []}
          columns={columns}
          rowKey={(r) => r.id}
          loading={history.isLoading}
          emptyTitle={m.onec_push_history_empty()}
          ariaLabel={m.onec_push_history_title()}
        />
      </section>
    </div>
  );
}
